import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";
import UserProfilePage from "../../users/profile/UserProfilePage";

const list = (value) => Array.isArray(value) ? value.filter(Boolean) : [];
const plainText = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
const compactDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, { month: "short", year: "numeric" }).format(date);
};
const serviceName = (service) => typeof service === "string"
  ? service
  : service?.briefName || service?.name || service?.code || "";

function ProfessionalResumeSnapshot({ profile = {} }) {
  const name = [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ");
  const location = [profile.city, profile.state, profile.country].filter(Boolean).join(", ");
  const summary = plainText(profile.profileSummary || profile.description || profile.experience);
  const workHistory = list(profile.workExperienceHistory);
  const education = list(profile.education);
  const credentials = list(profile.certifications);
  const capabilities = list(profile.interestedServices).map(serviceName).filter(Boolean);
  const role = profile.profileName || profile.sector?.name || profile.sectorCode || "Professional";

  return (
    <article className="professional-resume-snapshot">
      <header className="professional-resume-snapshot__header">
        {profile.profilePhoto ? <img src={profile.profilePhoto} alt="" /> : null}
        <div>
          <h1>{name || role}</h1>
          <p className="professional-resume-snapshot__role">{role}</p>
          <p className="professional-resume-snapshot__contact">
            {[profile.email, profile.phone, location].filter(Boolean).join("  |  ")}
          </p>
        </div>
      </header>

      {summary ? <section><h2>Professional Summary</h2><p>{summary}</p></section> : null}

      {workHistory.length ? (
        <section>
          <h2>Work Experience</h2>
          {workHistory.map((item, index) => (
            <div className="professional-resume-snapshot__entry" key={`${item.organization || item.role}-${index}`}>
              <div className="professional-resume-snapshot__entry-head">
                <h3>{item.role || "Professional experience"}</h3>
                <span>{[compactDate(item.startDate), item.isCurrent || item.current ? "Present" : compactDate(item.endDate)].filter(Boolean).join(" - ")}</span>
              </div>
              <strong>{[item.organization, item.location].filter(Boolean).join(" | ")}</strong>
              {plainText(item.notes) ? <p>{plainText(item.notes)}</p> : null}
            </div>
          ))}
        </section>
      ) : null}

      {capabilities.length ? <section><h2>Core Capabilities</h2><p>{capabilities.join(" | ")}</p></section> : null}

      {education.length ? (
        <section>
          <h2>Education</h2>
          {education.map((item, index) => (
            <div className="professional-resume-snapshot__entry" key={`${item.institution || item.program}-${index}`}>
              <div className="professional-resume-snapshot__entry-head">
                <h3>{item.program || item.educationLevel || "Education"}</h3>
                <span>{item.graduationYear || compactDate(item.endDate)}</span>
              </div>
              <strong>{item.institution}</strong>
            </div>
          ))}
        </section>
      ) : null}

      {credentials.length ? (
        <section>
          <h2>Licenses & Certifications</h2>
          <div className="professional-resume-snapshot__credentials">
            {credentials.map((item, index) => (
              <div key={`${item.name || item.credentialType}-${index}`}>
                <strong>{item.name || item.credentialType}</strong>
                <span>{[item.issuer, item.certificationNumber, item.expiryDate ? `Expires ${compactDate(item.expiryDate)}` : ""].filter(Boolean).join(" | ")}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}

function UserSnapshotIdentity({ profile = {} }) {
  const name = [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ");
  const photoUrl = profile.profilePhoto || (/^https?:\/\//i.test(String(profile.profilePhotoFileId || "")) ? profile.profilePhotoFileId : "");
  return (
    <section className="user-snapshot-identity">
      {photoUrl ? <img src={photoUrl} alt={`${name || "User"} profile`} /> : null}
      <div>
        <h1>{name || profile.username || "User Profile"}</h1>
        <p>{[profile.email, profile.phone].filter(Boolean).join(" | ")}</p>
      </div>
    </section>
  );
}

const localDateTime = (value) => {
  if (!value) return "Not recorded";
  const normalized = String(value)
    .replace(/\[.*\]$/, "")
    .replace(/(\.\d{3})\d+(?=Z$|[+-]\d{2}:\d{2}$)/, "$1");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

export default function CareStaffSnapshotPage({ careProfileIdOverride = "", typeOverride = "", embedded = false }) {
  const params = useParams();
  const careProfileId = careProfileIdOverride || params.careProfileId;
  const type = typeOverride || params.snapshotType;
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const title = type === "professional-profile" ? "Professional Profile Snapshot" : "User Profile Snapshot";

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    careService.careProfiles.getSnapshot(careProfileId, type)
      .then((result) => active && setSnapshot(result || null))
      .catch((err) => active && setError(err?.response?.data?.message || err?.message || "Unable to load saved snapshot."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [careProfileId, type]);

  const content = useMemo(() => (
    <>
      {!embedded ? (
        <div className="residents-page-head">
          <div className="page-title"><h1>{title}</h1><p>Workspace-owned copy of this staff profile.</p></div>
          <Link className="mini-btn" to={`/staff/${careProfileId}`}><i className="ti ti-arrow-left" /> Back to staff</Link>
        </div>
      ) : null}
      {loading ? <Panel title={title}><p>Loading saved snapshot...</p></Panel> : null}
      {error ? <Panel title={title}><p className="form-error">{error}</p></Panel> : null}
      {!loading && !error && snapshot ? (
        <>
          <div className="care-staff-snapshot-meta">
            <div><span>Captured</span><strong>{localDateTime(snapshot.capturedAt)}</strong></div>
            <div className="care-staff-snapshot-print-action">
              <button className="mini-btn" type="button" onClick={() => window.print()}>
                <i className="ti ti-printer" /> Print
              </button>
            </div>
          </div>
          {type === "professional-profile" ? (
            <ProfessionalResumeSnapshot profile={snapshot.data} />
          ) : (
            <>
              <UserSnapshotIdentity profile={snapshot.data} />
              <div className="user-profile-snapshot-details">
                <UserProfilePage
                  profileDataOverride={snapshot.data}
                  embedded
                  readOnly
                  hideDocumentSections
                />
              </div>
            </>
          )}
        </>
      ) : null}
    </>
  ), [careProfileId, embedded, error, loading, snapshot, title, type]);

  const printContent = snapshot ? (
    <div className="care-staff-snapshot-print-portal">
      {type === "professional-profile" ? (
        <ProfessionalResumeSnapshot profile={snapshot.data} />
      ) : (
        <>
          <UserSnapshotIdentity profile={snapshot.data} />
          <UserProfilePage
            profileDataOverride={snapshot.data}
            embedded
            readOnly
            hideDocumentSections
            showUnmaskedDateOfBirth
          />
        </>
      )}
    </div>
  ) : null;

  return (
    <>
      {embedded
        ? <div className="care-staff-snapshot-embedded care-staff-snapshot-print-root">{content}</div>
        : <CareShell className="dashboard-content residents-page care-staff-snapshot-page care-staff-snapshot-print-root">{content}</CareShell>}
      {printContent ? createPortal(printContent, document.body) : null}
    </>
  );
}
